import { Goto } from "@/shared/navigation";

/**
 * Specification for a client-side tool that executes in the browser.
 */
export interface ClientToolSpec {
  /** Unique name identifying the tool. */
  name: string;
  /** Human-readable description of what the tool does. */
  description: string;
  /** JSON Schema describing the tool's input parameters. */
  input_schema: Record<string, unknown>;
}

/**
 * Dependencies that client tools may need from React context.
 *
 * Pure functions can't call hooks, so the hook collects these deps and passes
 * them into the tool executor.
 */
export interface ClientToolDeps {
  goto: Goto;
}

/**
 * Execution result returned by every client tool's `execute` function.
 */
export interface ClientToolExecutionResult {
  response: Record<string, unknown>;
  success: boolean;
}

/**
 * Client tool interface used by the tool registry and dispatcher.
 *
 * Use {@link createClientTool} to build a `ClientTool` with type-safe
 * input validation rather than implementing this interface directly.
 */
export interface ClientTool {
  spec: ClientToolSpec;
  execute: (
    params: Record<string, unknown>,
    deps?: ClientToolDeps,
  ) => ClientToolExecutionResult;
}

/**
 * Configuration accepted by {@link createClientTool}.
 *
 * @typeParam T - The validated input type for the tool.
 */
export interface ClientToolConfig<T> {
  /** Unique name identifying the tool. */
  name: string;
  /** Human-readable description of what the tool does. */
  description: string;
  /** JSON Schema describing the tool's input parameters. */
  input_schema: Record<string, unknown>;
  /**
   * Optional type-guard that validates raw input before `execute` is called.
   *
   * When provided, the wrapper calls `validateInput(params)` first. If it
   * returns `false`, the tool returns a generic validation-error result
   * without ever calling `execute`.
   *
   * When omitted, all input is forwarded to `execute` unconditionally
   * (cast to `T`).
   */
  validateInput?: (raw: unknown) => raw is T;
  /**
   * Tool implementation that receives the validated input.
   *
   * If `validateInput` is provided and passes, `input` is guaranteed to
   * satisfy `T`. If `validateInput` is omitted, `input` is a blind cast
   * from the raw params — the tool author is responsible for defensive
   * checks inside `execute`.
   */
  execute: (input: T, deps?: ClientToolDeps) => ClientToolExecutionResult;
}

/**
 * Factory that builds a {@link ClientTool} from a typed configuration.
 *
 * The returned `ClientTool.execute` wraps the caller-supplied `execute`
 * with an optional `validateInput` guard. If validation fails, a generic
 * error result is returned without calling `execute`.
 *
 * @example
 * ```ts
 * interface SearchInput { query: string }
 *
 * const searchTool = createClientTool<SearchInput>({
 *   name: "search",
 *   description: "Search datasets",
 *   input_schema: {
 *     type: "object",
 *     properties: { query: { type: "string" } },
 *     required: ["query"],
 *   },
 *   validateInput: (raw): raw is SearchInput =>
 *     typeof raw === "object" &&
 *     raw !== null &&
 *     "query" in raw &&
 *     typeof raw.query === "string",
 *   execute(input) {
 *     return { response: { results: [] }, success: true };
 *   },
 * });
 * ```
 */
export function createClientTool<T>(config: ClientToolConfig<T>): ClientTool {
  return {
    spec: {
      name: config.name,
      description: config.description,
      input_schema: config.input_schema,
    },
    execute(params, deps) {
      if (config.validateInput && !config.validateInput(params)) {
        return {
          response: { error: "Invalid input" },
          success: false,
        };
      }

      return config.execute(params as T, deps);
    },
  };
}
